'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';

import MasonryGallery from '@/components/global/MasonryGallery';
import { PageCareerOverviewSchema } from '@/sanity/pages/PageCareerOverviewTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function CareerStatsSection({ data }: { data: PageCareerOverviewSchema }) {
  const cards = data.statsCards ?? [];
  if (!data.statsTitle && cards.length === 0) return null;

  return (
    <section className="w-full bg-white px-[2rem] py-[8rem] lg:px-[6rem] lg:py-[15rem]">
      <div className="mx-auto flex w-[min(169.5rem,100%)] flex-col items-center">
        {data.statsChip && (
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease }}
            className="mb-0 text-center font-blacker text-[2rem] italic leading-[1.5] text-black"
          >
            {data.statsChip}
          </motion.p>
        )}

        {data.statsTitle && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="richtext mt-[1.875rem] w-full prose-headings:mb-0 prose-headings:text-[4rem] prose-headings:leading-[1.15] prose-headings:!font-blacker prose-headings:tracking-[-0.25rem] lg:mt-[3rem] lg:prose-headings:!font-geist lg:prose-headings:text-[6.5rem] lg:prose-headings:leading-[0.98] lg:prose-headings:tracking-[-0.3rem]"
          >
            <PortableText value={data.statsTitle} components={richTextComponent} />
          </motion.div>
        )}

        {cards.length > 0 && (
          <MasonryGallery
            items={cards}
            rowHeights={['36.125rem', '37.5rem', '35.625rem']}
            // 12 unequal tracks, so the tiles' spans (7+5, 5+7, 3+4) land on the
            // design's exact widths (749/497, 524/722, 310/396 at 16px gaps) —
            // equal columns can't produce those.
            columns="repeat(5, 92fr) repeat(2, 96.5fr) 85fr repeat(4, 87fr)"
            className="mt-[4rem] w-full max-w-[157.75rem] lg:mt-[5rem]"
          />
        )}
      </div>
    </section>
  );
}
